import { IProject } from '../../interfaces/project.interface'
import { Role } from '../../models'
import { Text, ViewStyle, StyleProp, StyleSheet, Platform, TouchableOpacity } from 'react-native'
import { useState, useEffect } from 'react'
import GroupedRecordMenu from './GroupedRecordMenu/GroupedRecordMenu'
import useDimensions from '../../hooks/useDimensions'
import { getStyleSheet } from '../../assets/theme/styles'
import { MomentaryView } from './DataTable'
import { Hoverable } from 'react-native-web-hover'
import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import ColorPickerNative from './ColorPicker'

interface IRowProject {
    project: IProject
    allClients: any
    allGroups: any
    userRole: Role
    onUpdateRow: any
    isReadOnly: boolean
    loading?: boolean
    style?: StyleProp<ViewStyle>
}

const cursorPointerStyle = Platform.OS === 'web' && { cursor: 'pointer', transition: 'all .1s ease-in' }

type RootStackParamList = {
    ProjectDetails: { projectId: string }
}

const RowProject = ({
    project,
    allClients,
    allGroups,
    userRole,
    onUpdateRow,
    isReadOnly,
    style,
    loading,
}: IRowProject) => {
    const isAdmin = userRole === Role.ADMIN
    const [valueName, setValueName] = useState<string>('')
    const { windowWidth } = useDimensions()
    const styles = getStyleSheet()
    const desktopSize = 1200
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>()

    const actions = [
        {
            action: () => onUpdateRow(project.id),
            label: isReadOnly ? 'Restore' : 'Archived',
            icon: isReadOnly ? 'restore' : 'archive',
        },
    ]

    useEffect(() => {
        if (project.name !== valueName) setValueName(project.name)
    }, [project])

    const onHandleChangeColor = (color: string, objectId: string, version: number) => {
        const objectUpdate = {
            id: objectId,
            groupcolor: color.replace('#', ''),
            _version: version,
        }
        if (project.groupcolor === color.replace('#', '')) return
        onUpdateRow(null, objectUpdate)
    }

    return (
        <Hoverable>
            {({ hovered }) => {
                return (
                    <TouchableOpacity
                        onPress={() => navigation.navigate('ProjectDetails', { projectId: project.id } as any)}
                        style={[
                            hovered ? { ...localStylesProject.boxShadow, shadowColor: styles.text } : null,
                            {
                                backgroundColor: styles.backgroundColorRow,
                                gap: 10,
                                borderRadius: 6,
                                padding: 10,
                            },
                            windowWidth > desktopSize ? localStylesProject.containerWhenThereOneFile : null,
                            style,
                        ]}
                    >
                        <MomentaryView
                            showViewUp={desktopSize}
                            customStyles={{
                                paddingHorizontal: windowWidth <= 500 ? 12 : 0,
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 20,
                            }}
                        >
                            <ColorPickerNative
                                disabled
                                customStyles={{ width: 36 }}
                                value={project.groupcolor}
                                identificator={`${project.id}-color`}
                                loading={loading}
                                skeletonStyles={{ width: 36 }}
                                onChange={color => onHandleChangeColor(color, project.id, project._version)}
                            />

                            <Text style={{ flexGrow: 1, color: styles.text, marginLeft: 10 }}>{project.name}</Text>

                            {windowWidth <= desktopSize ? (
                                <GroupedRecordMenu
                                    disabled={!isAdmin}
                                    buttonTitleStyles={{ paddingHorizontal: 10 }}
                                    identificator={project.id}
                                    items={actions}
                                    loading={loading}
                                    skeletonStyles={{ width: 26 }}
                                />
                            ) : null}
                        </MomentaryView>

                        <MomentaryView
                            customStyles={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                paddingHorizontal: windowWidth <= 500 ? 12 : 0,
                            }}
                            showViewUp={desktopSize}
                        >
                            <Text style={{ width: windowWidth <= desktopSize ? '50%' : '20%', color: styles.text }}>
                                {project.clientName || '- no clients -'}
                            </Text>

                            <Text style={{ width: windowWidth <= desktopSize ? '50%' : '20%', color: styles.text }}>
                                {project.clientName || '- no groups -'}
                            </Text>
                        </MomentaryView>

                        {windowWidth > desktopSize ? (
                            <GroupedRecordMenu
                                disabled={!isAdmin}
                                loading={loading}
                                skeletonStyles={{ width: 26 }}
                                identificator={project.id}
                                items={actions}
                            />
                        ) : null}
                    </TouchableOpacity>
                )
            }}
        </Hoverable>
    )
}

export const localStylesProject = StyleSheet.create({
    containerWhenThereOneFile: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 20,
        padding: 0,
        paddingVertical: 16,
        paddingHorizontal: 20,
    },
    boxShadow: {
        ...(cursorPointerStyle as any),
        shadowRadius: 6,
        shadowOffset: {
            width: 0,
            height: 0,
        },
        elevation: 5,
    },
})

export default RowProject
